import { CMSComponents, CMSComponentTypes } from '@components/cms';
import { Head } from '@components/common/head';
import { SectionSeparator } from '@styles/layout';
import React from 'react';
import styled from '@emotion/styled';
import { HelpBreadcrumb } from '@components/breadcrumbs/help';
import { TextMarkdown } from '@components/common/description';
import { ContentLayout } from '@components/common/layout';
import { PageTitle } from '@components/common/page-title';
import { Footer } from '@components/footer';
import { PageProps } from '@model/pages';
import { ImagesResponse } from '@model/iceberg';
import { DestinationHero } from '@components/common/hero/destination/DestinationHero';
import { Error500 } from '@components/common/hero/error/Error500';
import { ProductHero } from '@components/common/hero/product/ProductHero';
import { NavBarContainer } from '@components/navigation';
import SearchTabsBar from '@components/search/SearchTabsBar';
import { breakpoints, mq } from '@styles/breakpoints';
import { getImageUrl } from '@util/images';
import { ThemeProps } from '@theme/base';
import { ERROR_CODES } from '@model/common/error-codes';

/* ***************** *
 *       Types       *
 * ***************** */
export interface GenericHelpPageLayoutProps extends PageProps {
  cmsConfig: any;
  hero: ImagesResponse;
  path: string;
}

/* ****************** *
 *       Styles       *
 * ****************** */

const Container = styled.div(({ theme }: ThemeProps) => ({
  overflowWrap: 'break-word',
  table: {
    ...(theme.custom.typography.labels.small as any),
    [mq.small]: {
      ...(theme.custom.typography.paragraph as any)
    }
  }
}));

const StyledTitle = styled(PageTitle)(({ theme }: ThemeProps) => ({
  ['h1']: {
    display: 'block',
    marginBottom: theme.custom.spacing.xLarge
  }
}));

/* ********************* *
 *    GenericHelpPage    *
 * ********************* */
export const GenericHelpPageLayout = ({ cmsConfig, hero, path, promoBannerTripType }: GenericHelpPageLayoutProps) => {
  if (!cmsConfig) {
    return <Error500 code={ERROR_CODES.NO_CMS_CONFIG} />;
  }

  const cmsHero = cmsConfig.hero && {
    title: cmsConfig.hero.fields.title,
    url: getImageUrl(cmsConfig.hero.fields.file.url, breakpoints.large, false, true)
  };

  return (
    <Container>
      <Head metaTitle={cmsConfig.metaTitle} metaDescription={cmsConfig.metaDescription} />
      {cmsHero ? (
        <>
          <NavBarContainer promoBannerTripType={promoBannerTripType} renderSearch={<SearchTabsBar condensed />} />
          <ProductHero hero={cmsHero} />
        </>
      ) : (
        <DestinationHero src={hero?.images[0]} />
      )}
      <HelpBreadcrumb path={path} />
      <SectionSeparator />
      <ContentLayout
        maxWidth
        main={
          <div>
            <StyledTitle heading={cmsConfig.title} boldHeading />
            {cmsConfig.description && (
              <>
                <TextMarkdown description={cmsConfig.description} />
                <SectionSeparator />
              </>
            )}

            <CMSComponents components={cmsConfig.components} />
          </div>
        }
        sidebar={<CMSComponents components={cmsConfig.sidebar} cmsComponentsType={CMSComponentTypes.SIDEBAR} />}
      />
      <SectionSeparator />
      <Footer />
    </Container>
  );
};
